import { Column as RTColumn } from '@tanstack/react-table'
import { IColumnVisibility } from 'components/DataTable/types'
import { useAuth } from 'context/authentication/useAuth'
import { createStore } from 'zustand'
import { persist } from 'zustand/middleware'
import { reconcileStoredColumns } from './reconcileColumns'
import { useColumnStoreContext } from './useColumnsStoreContext'

export interface Column {
  id: string
  title: string
  isEditable: boolean
  isHidden: boolean
  isFirstColumn?: boolean
  isLastColumn?: boolean
}

export interface ColumnGroup {
  id: string
  title: string
  columns: Column[]
}

interface ColumnState {
  groups: ColumnGroup[]
  tableKey: string
  savedPresetsIdentifier?: string
  defaultColumnVisibility?: IColumnVisibility<unknown>
  selectedColumns?: Column[]
  defaultSelectedColumns?: Column[]
  preferredGroupOrder?: string[]
  initialColumnOrder?: string[]
  // Track columns that existed when user last saved preferences
  // Used to distinguish "user hid this" from "this is a new column"
  knownColumnIds?: string[]
}

interface ColumnActions {
  setSelectedColumns: (columns: Column[]) => void
  initializeTableColumns: (columns: RTColumn<unknown, unknown>[]) => void
}

export type ColumnStoreInternal = ColumnState & ColumnActions

export const createColumnsStore = <T>({
  tableKey,
  savedPresetsIdentifier,
  defaultColumnVisibility,
  initialColumnOrder,
  preferredGroupOrder,
}: {
  tableKey: string
  savedPresetsIdentifier?: string
  defaultColumnVisibility?: IColumnVisibility<T>
  initialColumnOrder?: string[]
  preferredGroupOrder?: string[]
}) => {
  const { user } = useAuth()
  const LOCAL_STORAGE_KEY_PREFIX = `${user?.id}-columns-${tableKey}`
  return createStore<ColumnStoreInternal>()(
    persist(
      (set, get) => ({
        groups: [] as ColumnGroup[],
        defaultColumnVisibility,
        tableKey,
        preferredGroupOrder,
        savedPresetsIdentifier: savedPresetsIdentifier,
        initialColumnOrder,
        selectedColumns: undefined,
        defaultSelectedColumns: undefined,
        knownColumnIds: undefined,
        setSelectedColumns: (selectedColumns) => {
          // Save both selected columns and all known column IDs
          // This lets us distinguish "user hid this" from "new column"
          const allColumns = get().groups.flatMap((group) => group.columns)
          set({
            selectedColumns,
            knownColumnIds: allColumns.map((c) => c.id),
          })
        },
        initializeTableColumns: (columns) => {
          const columnGroups = columns.reduce(
            (acc, column) => {
              const meta = (column.columnDef.meta || {}) as {
                headerTitle?: string
                headerGroup?: string
                headerDisableOrdering?: boolean
                isEditable?: boolean
                isFirstColumn?: boolean
                isLastColumn?: boolean
              }

              const { headerGroup } = {
                headerGroup: 'Ungrouped', // Default group name
                ...meta,
              }
              if (headerGroup) {
                if (!acc[headerGroup]) {
                  acc[headerGroup] = {
                    id: headerGroup,
                    title: headerGroup,
                    columns: [],
                  }
                }
                const columnTitle =
                  typeof column.columnDef.header === 'function'
                    ? meta.headerTitle
                    : column.columnDef.header

                acc[headerGroup].columns.push({
                  id: column.id ?? columnTitle!,
                  title: columnTitle!,
                  isEditable: meta?.isEditable ?? true,
                  isHidden:
                    column.columnDef.id === 'expanded-row' ||
                    meta?.headerDisableOrdering ||
                    false,
                  isFirstColumn: meta?.isFirstColumn ?? false,
                  isLastColumn: meta?.isLastColumn ?? false,
                })
              }
              return acc
            },
            {} as Record<string, ColumnGroup>
          )

          let sortedGroups = Object.values(columnGroups)
          if (preferredGroupOrder) {
            // Order following the preferredGroupOrder, if not found, add to the end
            sortedGroups = sortedGroups.sort((a, b) => {
              const aIndex = preferredGroupOrder.indexOf(a.id)
              const bIndex = preferredGroupOrder.indexOf(b.id)
              return (
                (aIndex === -1 ? Infinity : aIndex) -
                (bIndex === -1 ? Infinity : bIndex)
              )
            })
          }

          let defaultSelectedColumns = sortedGroups
            .flatMap((group) => group.columns)
            .filter((column) => {
              const hideColumn =
                defaultColumnVisibility?.[
                  column.id as keyof typeof defaultColumnVisibility
                ] === false || column.isHidden
              return !hideColumn
            })

          if (initialColumnOrder) {
            // Order following the initialColumnOrder, if not found, add to the end
            defaultSelectedColumns = defaultSelectedColumns.sort((a, b) => {
              const aIndex = initialColumnOrder.indexOf(a.id)
              const bIndex = initialColumnOrder.indexOf(b.id)
              return (
                (aIndex === -1 ? Infinity : aIndex) -
                (bIndex === -1 ? Infinity : bIndex)
              )
            })
          }

          // Reconcile stored columns with current columns
          // This handles adding new columns and removing stale ones from localStorage
          const allColumns = sortedGroups.flatMap((group) => group.columns)
          const { reconciledColumns, reconciledKnownIds, hasChanges } =
            reconcileStoredColumns(
              get().selectedColumns,
              get().knownColumnIds,
              allColumns,
              defaultColumnVisibility
            )

          if (hasChanges) {
            set({
              selectedColumns: reconciledColumns,
              knownColumnIds: reconciledKnownIds,
              defaultSelectedColumns,
              groups: sortedGroups,
            })
          } else {
            set({
              defaultSelectedColumns,
              groups: sortedGroups,
            })
          }
        },
      }),
      {
        name: LOCAL_STORAGE_KEY_PREFIX,
        partialize: (state) => ({
          // Persist column preferences to localStorage
          selectedColumns: state.selectedColumns,
          knownColumnIds: state.knownColumnIds,
        }),
      }
    )
  )
}

export type ColumnStore = ReturnType<typeof createColumnsStore>

export function getDefaultSelectedColumns() {
  const groups = useColumnStoreContext((state) => state.groups)
  const defaultColumnVisibility = useColumnStoreContext(
    (state) => state.defaultColumnVisibility
  )
  const selectedColumns = useColumnStoreContext(
    (state) => state.selectedColumns
  )
  const defaultSelectedColumns = useColumnStoreContext(
    (state) => state.defaultSelectedColumns
  )

  if (selectedColumns) {
    return selectedColumns
  }

  if (defaultSelectedColumns) {
    return defaultSelectedColumns
  }

  return groups
    .flatMap((group) => group.columns)
    .filter((column) => {
      const hideColumn =
        defaultColumnVisibility?.[
          column.id as keyof typeof defaultColumnVisibility
        ] === false || column.isHidden
      return !hideColumn
    })
}

export function getColumnVisibility() {
  const defaultColumnVisibility = useColumnStoreContext(
    (state) => state.defaultColumnVisibility
  )
  const groups = useColumnStoreContext((state) => state.groups)
  const selectedColumns = useColumnStoreContext(
    (state) => state.selectedColumns
  )
  const knownColumnIds = useColumnStoreContext((state) => state.knownColumnIds)

  // No saved preferences yet (first-time user) - use defaults
  if (!knownColumnIds) {
    return defaultColumnVisibility
  }

  // User has saved before - respect their choices
  const allColumns = groups.flatMap((group) => group.columns)
  const selectedIds = new Set(selectedColumns?.map((c) => c.id) ?? [])
  const knownIds = new Set(knownColumnIds)

  return allColumns.reduce(
    (acc, column) => {
      let isVisible: boolean
      if (selectedIds.has(column.id)) {
        // User selected this column
        isVisible = true
      } else if (knownIds.has(column.id)) {
        // User explicitly hid this column (it was known but not selected)
        isVisible = false
      } else {
        // New column (not in knownIds) - use defaultColumnVisibility
        isVisible =
          defaultColumnVisibility?.[
            column.id as keyof typeof defaultColumnVisibility
          ] ?? false
      }
      return { ...acc, [column.id]: isVisible }
    },
    {} as Record<string, boolean>
  )
}

export function getColumnOrder() {
  const groups = useColumnStoreContext((state) => state.groups)
  const selectedColumns = useColumnStoreContext(
    (state) => state.selectedColumns
  )
  const defaultSelectedColumns = useColumnStoreContext(
    (state) => state.defaultSelectedColumns
  )
  const columnsToUse = selectedColumns ?? defaultSelectedColumns

  // Keep the order of all columns, only updating the order of selected columns
  const allColumns = groups.flatMap((group) => group.columns)

  const sortedColumns = allColumns.sort((a, b) => {
    // Check for isFirstColumn attribute
    if (a.isFirstColumn) return -1
    if (b.isFirstColumn) return 1

    if (a.isLastColumn) return 1
    if (b.isLastColumn) return -1

    const aIndex = columnsToUse?.findIndex(
      (selectedColumn) => selectedColumn.id === a.id
    )
    const bIndex = columnsToUse?.findIndex(
      (selectedColumn) => selectedColumn.id === b.id
    )
    return (aIndex ?? -1) - (bIndex ?? -1)
  })

  return sortedColumns.map((column) => column.id)
}

export function getReadOnlyColumns() {
  const groups = useColumnStoreContext((state) => state.groups)

  return groups
    .flatMap((group) => group.columns)
    .filter((column) => !column.isEditable && !column.isHidden)
}

export function getHiddenColumns() {
  const groups = useColumnStoreContext((state) => state.groups)

  return groups
    .flatMap((group) => group.columns)
    .filter((column) => column.isHidden)
}

export function getAllColumns() {
  const groups = useColumnStoreContext((state) => state.groups)
  return groups.flatMap((group) => group.columns)
}
